/**
 * Pipeline Overview Page
 * 
 * Displays the Lead Pipeline overview with:
 * - 1 Pre-Stage (System) shown in a distinct section
 * - 10 CRM Stages shown in order
 * - Brand selector for filtering by brand
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Settings, Target, Clock, CheckCircle } from 'lucide-react';
import { BrandSelector } from '../../components/pipeline/BrandSelector';
import { 
  getPreStage, 
  getCRMStages, 
  getOutcomes,
  getCRMStageCount 
} from '../../lib/leadPipelineRegistry';
import type { PipelineBrand, PipelineStageRegistry, PipelineOutcome } from '../../types';

const PipelineOverview: React.FC = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [selectedBrand, setSelectedBrand] = useState<PipelineBrand>('Changan');

  // Get data from registry
  const preStage = getPreStage();
  const crmStages = getCRMStages();
  const outcomes = getOutcomes();
  const stageCount = getCRMStageCount();

  // Filter stages by selected brand
  const filteredStages = crmStages.filter(stage => 
    stage.meta.appliesToBrands.includes(selectedBrand)
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isArabic ? 'نظرة عامة على مسار العملاء المحتملين' : 'Lead Pipeline Overview'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {isArabic 
              ? `مسار متكامل مكون من مرحلة نظام مسبقة + ${stageCount} مراحل CRM لإدارة العملاء المحتملين من الالتقاط إلى التسليم`
              : `Comprehensive pipeline with 1 System Pre-Stage + ${stageCount} CRM Stages from lead capture to delivery`
            }
          </p>
        </div>
        
        {/* Brand Selector */}
        <BrandSelector 
          selectedBrand={selectedBrand} 
          onBrandChange={setSelectedBrand}
        />
      </div>

      {/* Pipeline Model Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 rounded-xl p-6 border border-indigo-100 dark:border-indigo-800">
        <h2 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-3">
          {isArabic ? 'نموذج المسار' : 'Pipeline Model'}
        </h2>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-indigo-600" />
            <span className="text-gray-700 dark:text-gray-300">
              {isArabic ? '1 مرحلة نظام مسبقة' : '1 System Pre-Stage'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-green-600" />
            <span className="text-gray-700 dark:text-gray-300">
              {isArabic ? `${stageCount} مراحل CRM` : `${stageCount} CRM Stages`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600" />
            <span className="text-gray-700 dark:text-gray-300">
              {isArabic ? `${outcomes.length} نتائج` : `${outcomes.length} Outcomes`}
            </span>
          </div>
        </div>
      </div>

      {/* Pre-Stage Section */}
      {preStage && (
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-500" />
            {isArabic ? 'مرحلة النظام المسبقة' : 'System Pre-Stage'}
          </h2>
          <PreStageCard stage={preStage} isArabic={isArabic} />
        </section>
      )}

      {/* CRM Stages Section */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-green-500" />
          {isArabic ? 'مراحل CRM' : 'CRM Stages'}
          <span className="text-sm font-normal text-gray-500">
            ({filteredStages.length} {isArabic ? 'مراحل' : 'stages'})
          </span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStages.map((stage) => (
            <StageCard key={stage.meta.id} stage={stage} isArabic={isArabic} />
          ))}
        </div>
      </section>

      {/* Outcomes Section */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-amber-500" />
          {isArabic ? 'النتائج (ليست مراحل)' : 'Outcomes (Not Stages)'}
        </h2>
        <div className="flex flex-wrap gap-3">
          {outcomes.map((outcome) => (
            <OutcomeBadge key={outcome.id} outcome={outcome} isArabic={isArabic} />
          ))}
        </div>
      </section>
    </div>
  );
};

/**
 * Pre-Stage Card Component
 */
const PreStageCard: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { meta, description, descriptionAr, kpis } = stage;
  
  return (
    <Link
      to={`/pipeline/stage/${meta.id}`}
      className="block bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 text-sm font-bold">
              0
            </span>
            <span className="text-xs font-medium uppercase tracking-wider opacity-80">
              {isArabic ? 'مرحلة النظام' : 'System Stage'}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">
            {isArabic ? meta.titleAr : meta.title}
          </h3>
          <p className="text-sm opacity-90 mb-3 line-clamp-2">
            {isArabic ? descriptionAr : description}
          </p>
          
          {/* Owner & SLA */}
          <div className="flex flex-wrap gap-4 text-xs">
            <div>
              <span className="opacity-70">{isArabic ? 'المالك: ' : 'Owner: '}</span>
              <span className="font-medium">{isArabic ? meta.ownerAr : meta.owner}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{isArabic ? meta.slaAr : meta.sla}</span>
            </div>
          </div>
        </div>
        <ArrowRight className={`w-5 h-5 opacity-70 ${isArabic ? 'rotate-180' : ''}`} />
      </div>
      
      {/* KPIs Badge */}
      <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-between">
        <span className="text-xs opacity-70">
          {kpis.length} {isArabic ? 'مؤشرات أداء' : 'KPIs'}
        </span>
        <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">
          {isArabic ? 'عرض التفاصيل' : 'View Details'}
        </span>
      </div>
    </Link>
  );
};

/**
 * Stage Card Component
 */
const StageCard: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { meta, description, descriptionAr, kpis, mandatoryFields } = stage;
  
  return (
    <Link
      to={`/pipeline/stage/${meta.id}`}
      className="group block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
    >
      <div className="flex items-start gap-3">
        {/* Stage Number */}
        <div 
          className="flex items-center justify-center w-10 h-10 rounded-lg text-white font-bold text-lg shrink-0"
          style={{ backgroundColor: meta.color }}
        >
          {meta.order}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Stage Title */}
          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {isArabic ? meta.titleAr : meta.title}
          </h3>
          
          {/* Owner */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {isArabic ? meta.ownerAr : meta.owner}
          </p>
        </div>
        
        <ArrowRight className={`w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors ${isArabic ? 'rotate-180' : ''}`} />
      </div>
      
      {/* Description */}
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
        {isArabic ? descriptionAr : description}
      </p>
      
      {/* Badges */}
      <div className="mt-4 flex flex-wrap gap-2">
        {/* SLA Badge */}
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          <Clock className="w-3 h-3" />
          {isArabic ? meta.slaAr.substring(0, 30) : meta.sla.substring(0, 30)}
          {(isArabic ? meta.slaAr : meta.sla).length > 30 ? '...' : ''}
        </span>
        
        {/* KPIs Count */}
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
          {kpis.length} {isArabic ? 'مؤشرات' : 'KPIs'}
        </span>
        
        {/* Fields Count */}
        {mandatoryFields.length > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {mandatoryFields.length} {isArabic ? 'حقول' : 'Fields'}
          </span>
        )}
      </div>
    </Link>
  );
};

/**
 * Outcome Badge Component
 */
const OutcomeBadge: React.FC<{ outcome: PipelineOutcome; isArabic: boolean }> = ({ 
  outcome, 
  isArabic 
}) => {
  // Color mapping for outcomes
  const colors: Record<string, string> = {
    won: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    lost: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    nurture: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    duplicate: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    invalid: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    unreachable: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
  };
  
  const colorClass = colors[outcome.id] || 'bg-gray-100 text-gray-800';
  
  return (
    <div 
      className={`inline-flex flex-col items-start px-4 py-2 rounded-lg ${colorClass}`}
      title={outcome.description}
    >
      <span className="font-medium">
        {isArabic ? outcome.titleAr : outcome.title}
      </span>
      <span className="text-xs opacity-75 mt-0.5">
        {outcome.requiresReasonCode 
          ? (isArabic ? 'يتطلب رمز السبب' : 'Requires reason code')
          : (isArabic ? 'لا يتطلب رمز السبب' : 'No reason code required')
        }
      </span>
    </div>
  );
};

export default PipelineOverview;
